/**
 * GESTION DE MATCHES DE INSUMOS, SIN SQL (MATCH-ADMIN-T1; pedido de Josse 11-ago).
 *
 * Contrato: P-CONTRATO-GESTION-MATCHES-MATCH-ADMIN-T1 v0.2 (95f561b1) + respuesta de A #27789.
 *   GET    /api/v1/businesses/{id}/insumo-matches              -> { asignaciones[], alias[] } de todo el negocio
 *   PATCH  /api/v1/businesses/{id}/proveedor-asignaciones/{id} -> { insumoId } o { estado: 'ACTIVO'|'INACTIVO' }
 *   PATCH  /api/v1/businesses/{id}/insumo-alias/{id}           -> { insumoId } o { factorEmpaque }
 *   DELETE /api/v1/businesses/{id}/insumo-alias/{id}           -> eco de la fila borrada (se deshace con el POST)
 *
 * LO QUE HAY QUE DECIR SIEMPRE (medido por A en Purchase V1.2, #27789):
 *   - Nada de esto cambia facturas ya cargadas: cambia la PROXIMA.
 *   - Desactivar una asignacion no es para siempre: si alguien vuelve a aprobar ese producto de ese proveedor, el
 *     nodo "Asignaciones Insumos" la deja ACTIVO otra vez.
 *
 * Los cuerpos se arman AQUI, en camelCase (ASP.NET no liga snake_case: la leccion del cliente_nombre).
 * Sin DOM.
 * @module application/compras/gestionMatches
 */

const pick = (o, ...ks) => {
  if (!o || typeof o !== 'object') return undefined;
  for (const k of ks) if (o[k] !== undefined) return o[k];
  return undefined;
};
const num = (v) => (v === null || v === undefined || v === '' || !Number.isFinite(Number(v)) ? null : Number(v));
const txt = (v) => (v === null || v === undefined ? '' : String(v).trim());

/** Solo el dueno (manager) y el admin de la plataforma gestionan matches; el colaborador no. */
export function puedeGestionar(rol) {
  return ['manager', 'admin', 'dev'].includes(String(rol || ''));
}

function asignacionDe(a) {
  const id = pick(a, 'id', 'Id');
  if (id === undefined || id === null) return null;
  return {
    id: String(id),
    insumoId: txt(pick(a, 'insumoId', 'insumo_id', 'InsumoId')),
    insumoNombre: txt(pick(a, 'insumoNombre', 'insumo_nombre', 'InsumoNombre')) || '(insumo sin nombre)',
    proveedorNombre: txt(pick(a, 'proveedorNombre', 'proveedor_nombre', 'ProveedorNombre')) || '(proveedor sin nombre)',
    codigo: txt(pick(a, 'codigoProductoFactura', 'codigo_producto_factura', 'CodigoProductoFactura')),
    descripcion: txt(pick(a, 'descripcionRef', 'descripcion_ref', 'DescripcionRef')),
    estado: (txt(pick(a, 'estado', 'Estado')) || 'ACTIVO').toUpperCase(),
    confianza: num(pick(a, 'confianza', 'Confianza'))
  };
}

function aliasDe(a) {
  const id = txt(pick(a, 'id', 'Id'));
  if (!id) return null;
  return {
    id,
    insumoId: txt(pick(a, 'insumoId', 'insumo_id', 'InsumoId')),
    insumoNombre: txt(pick(a, 'insumoNombre', 'insumo_nombre', 'InsumoNombre')) || '(insumo sin nombre)',
    alias: txt(pick(a, 'alias', 'Alias')),
    factorEmpaque: num(pick(a, 'factorEmpaque', 'factor_empaque', 'FactorEmpaque'))
  };
}

/**
 * Agrupa por insumo: [{ insumoId, insumoNombre, asignaciones[], alias[] }], por nombre.
 * @returns {null|{grupos:Array, totalAsignaciones:number, totalAlias:number}}
 */
export function leerMatches(data) {
  const d = pick(data, 'data', 'Data') ?? data;
  const asig = pick(d, 'asignaciones', 'Asignaciones');
  const ali = pick(d, 'alias', 'Alias');
  if (!Array.isArray(asig) && !Array.isArray(ali)) return null;
  const grupos = new Map();
  const grupo = (x) => {
    const k = x.insumoId || x.insumoNombre;
    if (!grupos.has(k)) grupos.set(k, { insumoId: x.insumoId, insumoNombre: x.insumoNombre, asignaciones: [], alias: [] });
    return grupos.get(k);
  };
  const A = (asig || []).map(asignacionDe).filter(Boolean);
  const L = (ali || []).map(aliasDe).filter(Boolean);
  A.forEach((x) => grupo(x).asignaciones.push(x));
  L.forEach((x) => grupo(x).alias.push(x));
  return {
    grupos: [...grupos.values()].sort((a, b) => a.insumoNombre.localeCompare(b.insumoNombre, 'es')),
    totalAsignaciones: A.length,
    totalAlias: L.length
  };
}

/** Filtro del buscador: por insumo, proveedor, codigo, descripcion o alias. Sin texto, todos. */
export function filtrarGrupos(grupos, texto) {
  const q = txt(texto).toLowerCase();
  if (!q) return grupos;
  return grupos.filter((g) => [g.insumoNombre, ...g.asignaciones.flatMap((a) => [a.proveedorNombre, a.codigo, a.descripcion]),
    ...g.alias.map((a) => a.alias)].some((s) => String(s || '').toLowerCase().includes(q)));
}

// ---- cuerpos (lo que viaja) ----

export function cuerpoReasignar(insumoId) {
  const id = txt(insumoId);
  return id ? { insumoId: id } : null;
}

export function cuerpoEstado(activa) {
  return { estado: activa ? 'ACTIVO' : 'INACTIVO' };
}

/** El factor es > 0; acepta coma decimal ("1,5"); un factor de empaque no lleva miles. null si no sirve. */
export function cuerpoFactor(valor) {
  const s = String(valor ?? '').trim();
  const n = /^\d+([.,]\d+)?$/.test(s) ? Number(s.replace(',', '.')) : NaN;
  return Number.isFinite(n) && n > 0 ? { factorEmpaque: n } : null;
}

/** Deshacer un borrado de alias con el POST que ya existe (InsumoAliasCreateRequestDto: insumoId, alias). */
export function cuerpoDeshacerBorrado(eco) {
  const insumoId = txt(pick(eco, 'insumoId', 'insumo_id', 'InsumoId'));
  const alias = txt(pick(eco, 'alias', 'Alias'));
  return insumoId && alias ? { insumoId, alias } : null;
}

/**
 * Lo que hay que devolver al deshacer (Q #27808): el POST solo recrea insumoId + alias; el factor de empaque NO
 * vuelve solo, asi que va en un PATCH despues. El codigo de proveedor no tiene ruta que lo escriba: se dice.
 * @returns {null|{crear:{insumoId,alias}, factor:{factorEmpaque:number}|null, noVuelve:string|null}}
 */
export function planDeshacer(eco) {
  const crear = cuerpoDeshacerBorrado(eco);
  if (!crear) return null;
  const f = num(pick(eco, 'factorEmpaque', 'factor_empaque', 'FactorEmpaque'));
  const cod = txt(pick(eco, 'codigoProductoProveedor', 'codigo_producto_proveedor', 'CodigoProductoProveedor'));
  return { crear, factor: f !== null && f > 0 ? { factorEmpaque: f } : null, noVuelve: cod ? `el codigo de proveedor ${cod}` : null };
}

/**
 * Deshace un borrado: POST del alias y, si tenia factor, PATCH del factor sobre el id nuevo, con el eco cotejado.
 * Nunca dice "deshecho" si algo no volvio. cliente = { crearAlias(biz, cuerpo), cambiarAlias(biz, id, cuerpo) }.
 * @returns {Promise<{ok:boolean, completo:boolean, mensaje:string}>}
 */
export async function deshacerBorrado(cliente, biz, plan) {
  if (!plan) return { ok: false, completo: false, mensaje: 'No hay con que deshacer este borrado.' };
  const r = await cliente.crearAlias(biz, plan.crear);
  if (!r || !r.ok) return { ok: false, completo: false, mensaje: `No se pudo deshacer: ${(r && r.detalle) || 'error de la API'}` };
  const id = txt(pick(r.data, 'id', 'Id'));
  const faltas = [];
  if (plan.factor) {
    const rf = id ? await cliente.cambiarAlias(biz, id, plan.factor) : null;
    if (!rf || !rf.ok || !ecoCuadra(plan.factor, rf.data)) faltas.push(`su factor de empaque (${plan.factor.factorEmpaque}): ponlo otra vez`);
  }
  if (plan.noVuelve) faltas.push(`${plan.noVuelve} (no hay como devolverlo desde aqui)`);
  return faltas.length
    ? { ok: true, completo: false, mensaje: `El alias volvio, pero SIN ${faltas.join(' y sin ')}.` }
    : { ok: true, completo: true, mensaje: `Deshecho: "${plan.crear.alias}" volvio${plan.factor ? ` con su factor ${plan.factor.factorEmpaque}` : ''}.` };
}

// ---- lo que se le dice al dueno antes de guardar ----

const NO_CAMBIA = 'Las facturas ya cargadas no cambian.';

export function fraseConfirmacion(accion, x, destino) {
  if (accion === 'reasignar-asignacion') {
    return `Las proximas facturas de ${x.proveedorNombre}${x.codigo ? ` con el codigo ${x.codigo}` : ''} van a entrar como "${destino}". ${NO_CAMBIA}`;
  }
  if (accion === 'desactivar') {
    return `Las proximas facturas de ${x.proveedorNombre}${x.codigo ? ` con el codigo ${x.codigo}` : ''} ya no se emparejan solas con "${x.insumoNombre}": van a pedir que alguien escoja. Ojo: si alguien vuelve a aprobar ese producto de ese proveedor, se activa otra vez. ${NO_CAMBIA}`;
  }
  if (accion === 'activar') return `Las proximas facturas de ${x.proveedorNombre} vuelven a entrar solas como "${x.insumoNombre}". ${NO_CAMBIA}`;
  if (accion === 'reasignar-alias') return `Cuando una factura diga "${x.alias}", va a entrar como "${destino}". ${NO_CAMBIA}`;
  if (accion === 'borrar-alias') return `"${x.alias}" deja de reconocerse como "${x.insumoNombre}". Si alguien lo vuelve a emparejar a mano, se aprende otra vez. ${NO_CAMBIA}`;
  if (accion === 'factor') return `Cada "${x.alias}" de la factura va a contar como ${destino} de "${x.insumoNombre}" en las proximas facturas. ${NO_CAMBIA}`;
  return NO_CAMBIA;
}

/** ¿La fila que devolvio la API dice lo que se mando? (un ligado a medias guarda en silencio otra cosa) */
export function ecoCuadra(cuerpo, eco) {
  if (!cuerpo || !eco) return false;
  return Object.keys(cuerpo).every((k) => {
    const v = pick(eco, k, k.charAt(0).toUpperCase() + k.slice(1));
    return typeof cuerpo[k] === 'number' ? Number(v) === cuerpo[k] : String(v ?? '').toUpperCase() === String(cuerpo[k]).toUpperCase();
  });
}
